import Button from '@components/std-components/Button'
import Typography from '@components/std-components/Typography'
import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import { MessageDialog } from '@components/UI-Components/MessageDialog'
import { NewEditIcon } from '@components/UI-Components/svg'
import PopupComponent from '@components/std-components/Popup'
import { useProductStatus } from '@components/UI-Components/ProductCard/ProductStatusHook'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { PRODUCT_OPERATIONS_LOADERS } from '@modules/order/stores/view/List/ListDataStore'
import { ListUIStore } from '@modules/order/stores/view/List/ListUIStore'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getEnumValue } from '@utils/get-enum-value'
import { nav } from '@stores/nav'
import { productPriceStore } from '@stores/productPricesStore'
import { productCurveStore } from '@stores/productCurvesStore'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useCallback, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QtyNumberInput from '../../components/QtyNumberInput'
import CreateCopyTemplatePopup from './CreateCopyTemplatePopup'
import * as css from './DraftUnSizedQuantity.css'
import TemplateListPopup from './TemplateListPopup'
import isEqual from 'lodash/isEqual'
import GradingPopupContent from './GradingSelectionPopup'
import Icon from '@components/std-components/Icons'
import cn from 'classnames'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import { config } from '@config'

const DraftUnSizedQuantity: FC<CellItem> = ({ rowData, column }) =>
  useObserver(function useHook() {
    const {
      uiStore: {
        dataStore: { summary },
        isOrderEditable
      }
    } = useOrderContext()
    const viewContextVal = useViewContext()
    const {
      copiedTemplateQuantities,
      updateQuantityDraftQuantity,
      bulkUpdateDraftQuantity,
      deleteCopiedTemplate,
      copyTemplate,
      pasteTemplateQuantity,
      getBookedDoors,
      getSumOfDraftQuantityOfBookedDoors,
      dataStore: { itemIndexObjectStore }
    } = viewContextVal.uiStore as ListUIStore
    const {
      queryParams: { clientId, buyingSessionGroupId, orderType, clientType }
    } = nav
    const [openDialog, setOpenDialog] = useState(false)
    const [showSaveCopyTemplate, setShowSaveCopyTemplate] = useState(false)
    const [showCopiedTemplates, setShowCopiedTemplates] = useState(false)
    const [isGradingPopupOpen, setIsGradingPopupOpen] = useState(false)
    const [productCurves, setProductCurves] = useState(null)
    const { t } = useTranslation()
    const index = rowData?.id ? itemIndexObjectStore.getIndexByKey(rowData.id) : null
    const isSpittingInProgress = isNumber(index)
      ? itemIndexObjectStore.isObjectDataLoading(
          index,
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY
        )
      : false
    const isUpdatingDraftQuantity = isNumber(index)
      ? itemIndexObjectStore.isObjectDataLoading(
          index,
          PRODUCT_OPERATIONS_LOADERS.UPDATING_CLIENT_ORDER_DRAFT_QUANTITY
        )
      : false

    const priceData = productPriceStore.getPriceForProduct(rowData?.id)
    const isRetailView = clientType === getEnumValue(VIPClientType.Internal)
    const isDoorOrder = orderType === OrderType.Door
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (rowData?.id) {
        const curves = productCurveStore.getCurvesForProduct(
          rowData.id,
          clientId,
          buyingSessionGroupId
        )
        setProductCurves(curves)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, clientId, buyingSessionGroupId])

    const splitToDoorHandler = useCallback(() => {
      // updateQuantityDraftQuantity(rowData, Number(inputRef?.current?.value))
      const productDoorCurves = productCurves?.doorCurve ?? {}
      if (isEmpty(productDoorCurves) || isEmpty(productDoorCurves?.curves)) {
        setOpenDialog(true)
      } else {
        bulkUpdateDraftQuantity(
          summary,
          rowData.id,
          getNumberFromLocale(inputRef?.current?.value)
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, inputRef, productCurves])

    const rollUpProductQtyHandler = useCallback(async () => {
      await updateQuantityDraftQuantity(
        rowData,
        getSumOfDraftQuantityOfBookedDoors(rowData?.id),
        null,
        true
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData])

    const handleDialogClose = () => {
      setOpenDialog(false)
    }

    const handleGradingApply = (productQty, selectedGrading) => {
      if (!isEqual(selectedGrading, gradingAttributes)) {
        updateQuantityDraftQuantity(rowData, Number(productQty), selectedGrading)
      }
      setIsGradingPopupOpen(false)
    }

    const handleGradingCancel = () => {
      setIsGradingPopupOpen(false)
    }

    const noBookedDoors = getBookedDoors(rowData?.id, summary?.doorOrders).length === 0
    const isProductActive: boolean = useProductStatus(rowData)
    const noGradeCurves = isEmpty(productCurves?.gradeCurve ?? {})
    const cmRating = productCurves?.cmRatingCurve?.curves?.weight ?? ''
    const gradeCurves = productCurves?.gradeCurve?.curves || {}
    const gradingAttributes = rowData?.quantity?.gradingAttributes || {}
    const bsgHasGradeCurves =
      !isRetailView && productCurveStore.hasGradeCurves(clientId, buyingSessionGroupId)
    const isGradingEnabledForProduct =
      !isRetailView &&
      !isEmpty(gradeCurves) &&
      !isDoorOrder &&
      isProductActive &&
      isOrderEditable

    if (!rowData) return null
    const value = get(rowData, column.field)
    const draftQty = get(rowData, 'quantity.totalByDraftQuantity')
    return (
      <>
        <div className={css.draftQty}>
          <div className={css.inputWrapper}>
            <QtyNumberInput
              onChange={e =>
                updateQuantityDraftQuantity(
                  rowData,
                  getNumberFromLocale(e.target.value),
                  null
                )
              }
              onKeyDown={() => null}
              disabled={
                !rowData?.original?.active ||
                isUpdatingDraftQuantity ||
                !isOrderEditable ||
                isDoorOrder ||
                !isProductActive ||
                (!noGradeCurves && !isRetailView)
              }
              className={css.qtyInput}
              value={value || 0}
              ref={inputRef}
              highlightInput={
                config.highlightQtyDiscrepanciesInSHR === 'true' && value !== draftQty
              }
            />
            {isGradingEnabledForProduct ? (
              <div>
                <PopupComponent
                  trigger={
                    <div>
                      <Icon
                        imageSrc={NewEditIcon}
                        alt={'edit'}
                        className={css.editIcon}
                      />
                    </div>
                  }
                  contentStyle={{
                    width: '360px',
                    height: '300px',
                    padding: 0
                  }}
                  open={isGradingPopupOpen}
                  onOpen={() => setIsGradingPopupOpen(true)}
                  onClose={handleGradingCancel}
                  position='right center'
                  lockScroll
                  nested={true}
                >
                  <GradingPopupContent
                    cmRating={cmRating}
                    gradeCurves={gradeCurves}
                    gradingAttributes={gradingAttributes}
                    onApply={handleGradingApply}
                    onCancel={handleGradingCancel}
                  />
                </PopupComponent>
              </div>
            ) : null}
          </div>
          {
            <div className={css.summaryContainer}>
              <Typography variant={'body2'} classes={cn(css.summaryTxt, css.draftValue)}>
                {formatCurrencyValueByLocale(
                  priceData?.kpiCurrency
                    ? priceData?.kpiCurrency
                    : isRetailView
                    ? priceData?.rtlCurrency
                    : priceData?.whlCurrency,
                  rowData?.quantity?.totalValueByProductQuantity || 0
                )}
              </Typography>
              {isGradingEnabledForProduct && (
                <>
                  <Typography variant={'body2'} classes={css.summaryTxt}>
                    {!isEmpty(gradingAttributes) ? (
                      <div>
                        Grading: {gradingAttributes?.grade ?? ''} <br /> Target ROS is{' '}
                        {gradingAttributes?.grade}.
                      </div>
                    ) : (
                      <div>
                        {t(
                          'order.quantitiesAndBook.emptyGrading',
                          'No grading selected.'
                        )}
                      </div>
                    )}
                  </Typography>
                  <Typography variant={'body2'} classes={css.summaryTxt}>
                    {!isEmpty(gradingAttributes) ? (
                      <div>
                        Shelf life is {gradingAttributes?.shelfLife ?? ''} <br /> Buffer
                        is {gradingAttributes?.buffer ?? ''}%.
                      </div>
                    ) : (
                      <div>
                        {t(
                          'order.quantitiesAndBook.emptyShelf',
                          'Default shelf life is 1 and buffer is 0.'
                        )}
                      </div>
                    )}
                  </Typography>
                </>
              )}
            </div>
          }
          {!isDoorOrder && (
            <Button
              onClick={splitToDoorHandler}
              disabled={
                !rowData?.original?.active ||
                isSpittingInProgress ||
                !isOrderEditable ||
                noBookedDoors
              }
              variant='link'
              size='none'
              className={css['btn']}
            >
              <Typography variant='body2'>
                {t('order.splitProductQty', 'Split Product QTY')}
              </Typography>
            </Button>
          )}
          <div className='align-center column flex justify-center'>
            <div>
              {!productCurveStore.getCurveLoading(clientId, buyingSessionGroupId) &&
                !bsgHasGradeCurves &&
                !isGradingEnabledForProduct &&
                !isDoorOrder && (
                  <Button
                    onClick={rollUpProductQtyHandler}
                    disabled={isSpittingInProgress || !isOrderEditable || noBookedDoors}
                    variant='link'
                    size='none'
                    className={css['btn']}
                  >
                    <Typography variant='body2'>
                      {t('order.rollUp', 'Roll Up')}
                    </Typography>
                  </Button>
                )}
            </div>
            <div>
              <Button
                onClick={() => {
                  setShowSaveCopyTemplate(true)
                }}
                disabled={isSpittingInProgress || !isOrderEditable || noBookedDoors}
                variant='link'
                size='none'
                className={css['btn']}
              >
                <Typography variant='body2'>
                  {t('order.quantitiesAndBook.toTemplate', 'Copy as template')}
                </Typography>
              </Button>
            </div>
            <div>
              <Button
                onClick={() => {
                  setShowCopiedTemplates(true)
                }}
                disabled={
                  isSpittingInProgress ||
                  !isOrderEditable ||
                  !copiedTemplateQuantities?.length ||
                  noBookedDoors
                }
                size='none'
                variant='link'
                className={css['btn']}
              >
                <Typography variant='body2'>
                  {t('order.quantitiesAndBook.fromTemplate', 'Paste from template')}
                </Typography>
              </Button>
            </div>
          </div>
          {openDialog ? (
            <MessageDialog
              open={openDialog}
              title={t('common.label.notAllowed', 'Not Allowed')}
              message={t(
                'order.messages.splitToDoorErrorMessage',
                'Sorry, but there is no door distribution information available, please contact your showroom administrator for help.'
              )}
              handleClose={handleDialogClose}
              closeButtonTitle={t('bb.common.label.ok', 'Ok')}
            />
          ) : null}
          {showSaveCopyTemplate ? (
            <CreateCopyTemplatePopup
              open={showSaveCopyTemplate}
              onClose={() => {
                setShowSaveCopyTemplate(false)
              }}
              onSave={copyTemplateMeta => {
                copyTemplate(
                  rowData.id,
                  copyTemplateMeta,
                  summary.doorOrder
                    ? [{ id: summary.doorOrder.doorOrderId, door: summary.door }]
                    : summary.doorOrders
                )
                setShowSaveCopyTemplate(false)
              }}
            />
          ) : null}
          {showCopiedTemplates ? (
            <TemplateListPopup
              onSelect={selected => {
                setShowCopiedTemplates(false)
                pasteTemplateQuantity(
                  summary.clientOrder.clientOrderId || summary.clientOrder?.id,
                  rowData.id,
                  selected,
                  summary.doorOrder
                    ? [{ id: summary.doorOrder.doorOrderId, door: summary.door }]
                    : summary.doorOrders
                )
              }}
              options={copiedTemplateQuantities}
              recentOption={{}}
              open={showCopiedTemplates}
              onDelete={selected => {
                deleteCopiedTemplate(selected)
              }}
              onClose={() => {
                setShowCopiedTemplates(false)
              }}
            />
          ) : null}
        </div>
      </>
    )
  })

export default memo(DraftUnSizedQuantity)
